import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getFirestore, collection, query, where, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useComparison } from '../contexts/ComparisonContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import '../flaticon.css';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import SEO from '../components/SEO';
import { showNotification } from '../components/Notification';
import { useCompanySettings } from '../hooks/useCompanySettings';
import ScrollToTop from '../components/ScrollToTop';
import PreLoader from '../components/PreLoader';
import BreadcrumbNav from '../components/seo/BreadcrumbNav';
import PropertyGridSkeleton from '../components/PropertyGridSkeleton';
import { toJsDate } from '../utils/condominiumListUtils';
import { buildPropertyPath } from '../utils/seo/urlHelpers';
import { fetchPropertiesByIds, fetchCondominiumsByIds } from '../utils/comparisonStorage';

function Favorites() {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPreLoading, setIsPreLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const { addToComparison, removeFromComparison, isInComparison } = useComparison();
  const navigate = useNavigate();
  const location = useLocation();
  const { companyName, settingsReady } = useCompanySettings();
  const [shareModal, setShareModal] = useState({
    isOpen: false,
    property: null
  });
  const [shareListModal, setShareListModal] = useState({
    isOpen: false,
    shareUrl: ''
  });
  const [clearModal, setClearModal] = useState({
    isOpen: false
  });
  // Estados para animações
  const [, setIsLoaded] = useState(false);
  const [visibleElements, setVisibleElements] = useState({
    header: false,
    properties: false
  });
  
  // Estado para controlar visibilidade de cada card
  const [visibleCards, setVisibleCards] = useState({});

  // Refs para os elementos que serão observados
  const headerRef = useRef(null);
  const propertiesRef = useRef(null);
  
  // Refs para cada card de propriedade
  const propertyRefs = useRef({});

  const favoritesPageTitle = useMemo(() => {
    const name = (companyName || '').trim();
    if (name) return `${name} - Favoritos | Seus imóveis favoritos`;
    if (!settingsReady) return 'Carregando...';
    return 'Imobiliária - Favoritos | Seus imóveis favoritos';
  }, [companyName, settingsReady]);

  // Função para finalizar o pre-loading
  const handlePreLoadingComplete = () => {
    setIsPreLoading(false);
  };

  // Carrega os favoritos do usuário (imóveis e condomínios) — paralelo + cache de catálogo
  const loadFavorites = useCallback(async () => {
    setLoading(true);
    try {
      const db = getFirestore();

      if (!user) {
        const localProp = JSON.parse(localStorage.getItem('localFavorites') || '[]');
        const localCondo = JSON.parse(localStorage.getItem('localCondominiumFavorites') || '[]');

        const [props, condos] = await Promise.all([
          fetchPropertiesByIds(db, localProp),
          fetchCondominiumsByIds(db, localCondo),
        ]);

        const items = [
          ...props.map((p) => ({
            ...p,
            favoriteEntity: 'property',
            createdAt: toJsDate(p.createdAt),
            favoriteId: null,
          })),
          ...condos.map((c) => ({
            ...c,
            favoriteEntity: 'condominium',
            createdAt: toJsDate(c.createdAt),
            favoriteId: null,
          })),
        ];

        setFavorites(items.slice(0, 20));
        return;
      }

      const favoritesSnapshot = await getDocs(
        query(collection(db, 'favorites'), where('userId', '==', user.uid))
      );

      const propertyFavs = [];
      const condoFavs = [];
      favoritesSnapshot.docs.forEach((favDoc) => {
        const fdata = favDoc.data();
        if (fdata.condominiumId) {
          condoFavs.push({ id: String(fdata.condominiumId), favoriteId: favDoc.id });
        } else if (fdata.propertyId) {
          propertyFavs.push({ id: String(fdata.propertyId), favoriteId: favDoc.id });
        }
      });

      const [props, condos] = await Promise.all([
        fetchPropertiesByIds(
          db,
          propertyFavs.map((f) => f.id)
        ),
        fetchCondominiumsByIds(
          db,
          condoFavs.map((f) => f.id)
        ),
      ]);

      const propFavById = new Map(propertyFavs.map((f) => [f.id, f.favoriteId]));
      const condoFavById = new Map(condoFavs.map((f) => [f.id, f.favoriteId]));

      const items = [
        ...props.map((p) => ({
          ...p,
          favoriteEntity: 'property',
          favoriteId: propFavById.get(String(p.id)) || null,
          createdAt: toJsDate(p.createdAt),
        })),
        ...condos.map((c) => ({
          ...c,
          favoriteEntity: 'condominium',
          favoriteId: condoFavById.get(String(c.id)) || null,
          createdAt: toJsDate(c.createdAt),
        })),
      ];

      setFavorites(items.slice(0, 20));
    } catch (error) {
      console.error('Erro ao carregar favoritos:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Remove um favorito
  const removeFavorite = async (property) => {
    try {
      if (!user) {
        if (property.favoriteEntity === 'condominium') {
          const localCondo = JSON.parse(localStorage.getItem('localCondominiumFavorites') || '[]');
          localStorage.setItem(
            'localCondominiumFavorites',
            JSON.stringify(localCondo.filter((id) => id !== property.id))
          );
        } else {
          const localFavorites = JSON.parse(localStorage.getItem('localFavorites') || '[]');
          localStorage.setItem(
            'localFavorites',
            JSON.stringify(localFavorites.filter((id) => id !== property.id))
          );
        }
        setFavorites((prev) => prev.filter((fav) => fav.id !== property.id));
      } else {
        // Usuário logado - remove do Firestore
        const db = getFirestore();
        await deleteDoc(doc(db, 'favorites', property.favoriteId));
        
        // Remove da lista local
        setFavorites(prev => prev.filter(fav => fav.id !== property.id));
        
        // A atualização em tempo real será feita automaticamente pelo listener do Firestore
      }
    } catch (error) {
      console.error('Erro ao remover favorito:', error);
      alert('Erro ao remover dos favoritos');
    }
  };

  const listingPath = (item) => (item.favoriteEntity === 'condominium' ? '/condominios' : '/propriedades');
  const detailPath = (item) =>
    item.favoriteEntity === 'condominium'
      ? `/condominios/${item.id}`
      : buildPropertyPath(item);

  // Funções de compartilhamento
  const getPropertyUrl = (property) => {
    return `${window.location.origin}${detailPath(property)}`;
  };

  const getShareText = (property) => {
    const label = property.favoriteEntity === 'condominium' ? 'condomínio' : 'imóvel';
    return `Confira este ${label}: ${property.title} - ${property.address}`;
  };

  const shareToWhatsApp = (property) => {
    const url = getPropertyUrl(property);
    const text = getShareText(property);
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareToFacebook = (property) => {
    const url = getPropertyUrl(property);
    const text = getShareText(property);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}&hashtag=${encodeURIComponent('#imoveis #venda #aluguel')}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const shareToLinkedIn = (property) => {
    const url = getPropertyUrl(property);
    const text = getShareText(property);
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(property.title)}&summary=${encodeURIComponent(text)}`;
    window.open(linkedinUrl, '_blank', 'width=600,height=400');
  };

  const shareToEmail = (property) => {
    const url = getPropertyUrl(property);
    const text = getShareText(property);
    const subject =
      property.favoriteEntity === 'condominium'
        ? `Condomínio: ${property.title}`
        : `Imóvel: ${property.title}`;
    const body = `${text}\n\nVeja mais detalhes: ${url}`;
    const emailUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = emailUrl;
  };

  const copyLink = async (property) => {
    const url = getPropertyUrl(property);
    try {
      await navigator.clipboard.writeText(url);
      alert('Link copiado para a área de transferência!');
    } catch (error) {
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Link copiado para a área de transferência!');
    }
  };

  // Funções para gerenciar lista de favoritos
  const clearAllFavorites = () => {
    setClearModal({ isOpen: true });
  };

  const confirmClearAllFavorites = async () => {
    try {
      if (!user) {
        // Usuário não logado - limpa localStorage
        localStorage.removeItem('localFavorites');
        localStorage.removeItem('localCondominiumFavorites');
        setFavorites([]);
        setClearModal({ isOpen: false });
        showNotification('Lista de favoritos limpa com sucesso!', 'success', 3000);
      } else {
        // Usuário logado - limpa Firestore
        const db = getFirestore();
        const favoritesQuery = query(
          collection(db, 'favorites'),
          where('userId', '==', user.uid)
        );
        
        const favoritesSnapshot = await getDocs(favoritesQuery);
        
        // Remove todos os favoritos
        const deletePromises = favoritesSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
        
        setFavorites([]);
        setClearModal({ isOpen: false });
        showNotification('Lista de favoritos limpa com sucesso!', 'success', 3000);
      }
    } catch (error) {
      console.error('Erro ao limpar favoritos:', error);
      showNotification('Erro ao limpar lista de favoritos', 'error', 5000);
    }
  };

  const shareFavoritesList = async () => {
    try {
      // Verifica se há favoritos para compartilhar
      if (favorites.length === 0) {
        showNotification('Adicione pelo menos um imóvel aos favoritos para compartilhar a lista.', 'warning', 4000);
        return;
      }

      // Verifica se o usuário está logado
      if (!user) {
        navigate('/login', {
          state: {
            from: location,
            message:
              'Para compartilhar sua lista de favoritos, você precisa fazer login ou criar uma conta. Seus favoritos salvos localmente serão preservados após o login.',
          },
        });
        return;
      }

      // Gera um ID único para a lista compartilhada
      const shareId = `favorites_${user.uid}_${Date.now()}`;
      
      // Cria um documento na coleção de listas compartilhadas
      const db = getFirestore();
      await setDoc(doc(db, 'sharedLists', shareId), {
        userId: user.uid,
        userName: user.name || 'Usuário',
        type: 'favorites',
        propertyIds: favorites.map((fav) => fav.id),
        favoriteItems: favorites.map((fav) => ({
          id: fav.id,
          entity: fav.favoriteEntity || 'property'
        })),
        createdAt: new Date(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 horas
      });

      // Gera a URL pública
      const shareUrl = `${window.location.origin}/lista-compartilhada/${shareId}`;
      
      setShareListModal({
        isOpen: true,
        shareUrl: shareUrl
      });
    } catch (error) {
      console.error('Erro ao gerar link de compartilhamento:', error);
      showNotification('Erro ao gerar link de compartilhamento', 'error', 5000);
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareListModal.shareUrl);
      alert('Link copiado para a área de transferência!');
    } catch (error) {
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = shareListModal.shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Link copiado para a área de transferência!');
    }
  };

  const shareListToWhatsApp = () => {
    const message = `Confira minha lista de imóveis favoritos: ${shareListModal.shareUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Função para limpar listas expiradas
  const cleanupExpiredLists = async () => {
    try {
      const db = getFirestore();
      const expiredListsQuery = query(
        collection(db, 'sharedLists'),
        where('expiresAt', '<', new Date())
      );
      
      const expiredListsSnapshot = await getDocs(expiredListsQuery);
      
      if (!expiredListsSnapshot.empty) {
        const deletePromises = expiredListsSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
      }
    } catch (error) {
      console.error('Erro ao limpar listas expiradas:', error);
    }
  };

  // Carrega os favoritos quando o componente monta
  useEffect(() => {
    if (authLoading) return undefined;

    loadFavorites();

    let idleId = null;
    let timeoutId = null;
    if (user) {
      const runCleanup = () => {
        cleanupExpiredLists();
      };
      if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(runCleanup, { timeout: 4000 });
      } else {
        timeoutId = setTimeout(runCleanup, 800);
      }
    }

    return () => {
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [user, authLoading, loadFavorites]);

  // Sync multi-aba (local) — sem onSnapshot que duplicava N getDocs e ignorava condomínios
  useEffect(() => {
    if (user || authLoading) return undefined;

    const handleStorageChange = (e) => {
      if (e.key === 'localFavorites' || e.key === 'localCondominiumFavorites') {
        loadFavorites();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user, authLoading, loadFavorites]);

  useEffect(() => {
    if (authLoading || loading) return;
    setIsPreLoading(false);
  }, [loading, authLoading]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Controlar animações de entrada baseadas no scroll
  useEffect(() => {
    // Só inicia as animações após o pre-loading terminar
    if (!isPreLoading) {
      setIsLoaded(true);
      
      // Configuração do Intersection Observer
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.getAttribute('data-animate');
            if (elementId) {
              if (elementId === 'property-card') {
                const cardId = entry.target.getAttribute('data-card-id');
                setVisibleCards(prev => ({ ...prev, [cardId]: true }));
              } else {
                setVisibleElements(prev => ({ ...prev, [elementId]: true }));
              }
              observer.unobserve(entry.target);
            }
          }
        });
      }, observerOptions);

      // Função para configurar o observer
      const setupObserver = () => {
        // Observar elementos principais
        const elementsToObserve = [
          headerRef.current,
          propertiesRef.current
        ].filter(Boolean);

        elementsToObserve.forEach(element => {
          if (element) {
            observer.observe(element);
          }
        });

        // Observar cada card individual
        Object.values(propertyRefs.current).forEach(element => {
          if (element) {
            observer.observe(element);
          }
        });
      };

      // Configurar o observer após um delay
      const timer = setTimeout(setupObserver, 1000);

      return () => {
        clearTimeout(timer);
        observer.disconnect();
      };
    }
  }, [isPreLoading, favorites]);

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      {/* PreLoader Component */}
      <PreLoader 
        isLoading={isPreLoading} 
        onLoadingComplete={handlePreLoadingComplete}
      />
      
      {/* SEO Component */}
      <SEO 
        title={favoritesPageTitle}
        description="Veja e compartilhe todos os imóveis que você selecionou como favoritos."
        keywords="favoritos, imóveis salvos, lista de imóveis"
        canonicalUrl="/favoritos"
        pageType="website"
        robots="noindex,nofollow"
      />
      
      {/* Container com largura limitada */}
      <div className="max-w-[85rem] mx-auto p-6 w-full">
        {/* Breadcrumbs */}
        <div 
          ref={headerRef}
          data-animate="header"
          className={`mb-8 transition-all duration-1000 ${visibleElements.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <BreadcrumbNav
            items={[
              { name: 'Início', path: '/' },
              { name: 'Favoritos', current: true },
            ]}
          />
        </div>

        <h1 
          className={`text-xl sm:text-2xl lg:text-3xl font-montserrat font-semibold mb-2 text-center transition-all duration-1000 delay-200 ${visibleElements.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          Seus imóveis favoritos em um só lugar
        </h1>
        
        <p 
          className={`text-sm sm:text-base lg:text-lg text-gray-600 mb-4 text-center transition-all duration-1000 delay-300 ${visibleElements.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          Veja e compartilhe todos os imóveis que você selecionou.
        </p>

        {/* Aviso para usuários não logados */}
        {!user && favorites.length > 0 && (
          <div 
            className={`mb-8 text-center transition-all duration-1000 delay-350 ${visibleElements.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-3 bg-orange-100 text-orange-700 rounded-lg border border-orange-400">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 flex-shrink-0">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
              </svg>
              <span className="text-sm">
                Seus favoritos estão salvos localmente.{' '}
                <button
                  onClick={() => navigate('/login', { state: { from: location } })}
                  className="font-bold underline hover:text-orange-900 transition-colors inline-flex items-center gap-1"
                >
                  Faça login
                </button>
                {' '}para salvá-los permanentemente e compartilhar sua lista.
              </span>
            </div>
          </div>
        )}

        {/* Aviso sobre limite de favoritos */}
        {favorites.length >= 20 && (
          <div 
            className={`mb-8 text-center transition-all duration-1000 delay-350 ${visibleElements.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-800 rounded-lg border border-red-200">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <span className="text-sm font-medium">
                <strong>Limite atingido:</strong> Você atingiu o limite máximo de 20 favoritos. Remova alguns para adicionar novos.
              </span>
            </div>
          </div>
        )}

        {/* Botões de ação */}
        {favorites.length > 0 && (
          <div 
            className={`flex flex-col sm:flex-row justify-center gap-4 mb-8 transition-all duration-1000 delay-400 ${visibleElements.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <button
              onClick={shareFavoritesList}
              className="px-6 py-3 rounded-lg text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2"
              style={{
                backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                color: 'var(--btn-outline-text, #3B82F6)',
                border: '1px solid var(--btn-outline-border, #3B82F6)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
                e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
                e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
              Compartilhar Lista
            </button>

            <button
              onClick={clearAllFavorites}
              className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Limpar Favoritos
            </button>
          </div>
        )}

        {/* Lista de favoritos */}
        <div 
          ref={propertiesRef}
          data-animate="properties"
          className={`transition-all duration-1000 delay-400 ${visibleElements.properties ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {loading ? (
            <PropertyGridSkeleton count={8} />
          ) : favorites.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Nenhum favorito ainda
              </h3>
              <p className="text-gray-600 mb-6">
                Comece adicionando imóveis aos seus favoritos para vê-los aqui.
              </p>
              <Link
                to="/propriedades"
                className="inline-flex items-center px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Explorar imóveis
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 min-[560px]:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {favorites.map((property) => (
                <div 
                  key={property.id}
                  ref={(el) => {
                    if (el) {
                      propertyRefs.current[property.id] = el;
                    }
                  }}
                  data-animate="property-card"
                  data-card-id={property.id}
                  className={`
                    border rounded-lg overflow-hidden shadow-md bg-white transition-all duration-500 transform hover:shadow-[0_0_30px_-5px_rgba(0,0,0,0.2)]
                    min-h-[450px] flex flex-col
                    ${visibleCards[property.id] 
                      ? 'opacity-100 translate-y-0 scale-100' 
                      : 'opacity-0 translate-y-8 scale-95'
                    }
                  `}
                >
                  {/* Container da imagem */}
                  <div 
                    className="relative h-[220px] overflow-hidden cursor-pointer"
                    onClick={() => navigate(detailPath(property))}
                  >
                    <div className="w-full h-full transform transition-transform duration-700 hover:scale-110 relative">
                      <LazyLoadImage
                        src={property.coverImage || property.images[0]}
                        alt={property.title}
                        className="w-full h-full object-cover absolute inset-0"
                        wrapperClassName="w-full h-full block bg-gray-100"
                        effect="blur"
                        placeholder={
                          <div className="w-full h-full bg-gray-200 animate-pulse"></div>
                        }
                      />
                    </div>

                    {/* Preço e Status */}
                    <div className="absolute bottom-2 left-2 right-2 flex justify-between items-center">
                      <p className="text-white text-base font-lato font-medium bg-gray-900/50 px-2 py-0.5 rounded backdrop-blur-sm">
                        {property.purpose === 'sale' 
                          ? `R$ ${Number(property.price).toLocaleString()}`
                          : `R$ ${Number(property.price).toLocaleString()}/mês`
                        }
                      </p>
                    </div>

                    {/* Badges e informações sobrepostas na imagem */}
                    <div className="absolute top-2 left-2 flex gap-2">
                      <span className="bg-gray-900/50 text-white px-3 py-1 rounded text-sm backdrop-blur-sm">
                        {property.purpose === 'sale' ? 'Venda' : 'Aluguel'}
                      </span>
                      {property.showInHome && (
                        <span className="bg-yellow-500/90 text-white px-2 py-1 rounded text-sm backdrop-blur-sm flex items-center gap-1 group relative">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clipRule="evenodd" />
                          </svg>
                          <div className="opacity-0 bg-black text-white text-xs rounded py-1 px-2 absolute -bottom-8 left-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                            Imóvel em destaque
                          </div>
                        </span>
                      )}
                    </div>

                    {/* Ícones no topo direito */}
                    <div className="absolute top-2 right-2 flex gap-2">
                      {property.tour360?.type && (
                        <div className="bg-gray-900/50 px-2 py-1 rounded backdrop-blur-sm group relative">
                          <div className="opacity-0 bg-black text-white text-xs rounded py-1 px-2 absolute -bottom-10 right-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                            {property.tour360.type === 'matterport' ? 'Contém tour 3D/360°' : property.tour360.type === 'edm360' ? 'Contém tour 360°' : 'Contém tour 360° Marzipano'}
                          </div>
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white">
                            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M12 3C14.5 3 17 7 17 12C17 17 14.5 21 12 21C9.5 21 7 17 7 12C7 7 9.5 3 12 3Z" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M3 12H21" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M4.5 7.5H19.5" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M4.5 16.5H19.5" stroke="currentColor" strokeWidth="1.5" />
                          </svg>
                        </div>
                      )}
                      {property.videoUrl && (
                        <div className="bg-gray-900/50 px-2 py-1 rounded backdrop-blur-sm group relative">
                          <div className="opacity-0 bg-black text-white text-xs rounded py-1 px-2 absolute -bottom-10 right-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                            Contém vídeo
                          </div>
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-white">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                          </svg>
                        </div>
                      )}
                      {property.floorPlans && property.floorPlans.length > 0 && (
                        <div className="bg-gray-900/50 px-2 py-1 rounded backdrop-blur-sm group relative">
                          <div className="opacity-0 bg-black text-white text-xs rounded py-1 px-2 absolute -bottom-10 right-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                            Contém planta baixa
                          </div>
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 24 24" 
                            className="w-5 h-5 text-white" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="1.5"
                          >
                            <rect x="2" y="2" width="20" height="20" rx="2" />
                            <path d="M2 7h20" />
                            <path d="M12 7v15" />
                            <path d="M7 7v8" />
                            <path d="M17 7v8" />
                            <path d="M7 11h5" />
                            <path d="M12 15h5" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Conteúdo do card */}
                  <div className="p-4 flex flex-col flex-1 justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-500 flex items-center gap-2">
                          {property.favoriteEntity === 'condominium'
                            ? `CONDOMÍNIO - ${property.code}`
                            : (<>
                          {property.type === 'house' ? 'CASA' : 
                           property.type === 'apartment' ? 'APARTAMENTO' : 
                           property.type === 'loft' ? 'LOFT' :
                           property.type === 'warehouse' ? 'GALPÃO' : 
                           property.type === 'store' ? 'LOJA' : 
                           property.type === 'building' ? 'PRÉDIO' : 
                           property.type === 'office' ? 'SALA' : 
                           property.type === 'land' ? 'TERRENO' : 
                           property.type === 'farm' ? 'SÍTIO' : 
                           property.type === 'countryHouse' ? 'CHÁCARA' : 'IMÓVEL'} - {property.code}
                            </>)}
                          {property.displayTag && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase whitespace-nowrap ${
                              property.displayTag === 'reserved' ? 'bg-red-100 text-red-800' :
                              property.displayTag === 'preLaunch' ? 'bg-blue-100 text-blue-800' :
                              property.displayTag === 'launch' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {property.displayTag === 'reserved' ? 'Reservado' :
                               property.displayTag === 'preLaunch' ? 'Pré-lançamento' :
                               property.displayTag === 'launch' ? 'Lançamento' :
                               property.displayTag}
                            </span>
                          )}
                        </span>
                        
                        {/* Botões de ação */}
                        <div className="flex items-center gap-1">
                          {/* Favoritos - Remover */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFavorite(property);
                            }}
                            className="p-1 rounded-full hover:bg-gray-100 transition-colors group relative"
                            title="Remover dos favoritos"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-3.5 h-3.5 transition-colors text-red-500 group-hover:text-red-600"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                              />
                            </svg>
                          </button>

                          {/* Comparação (apenas imóveis) */}
                          {property.favoriteEntity !== 'condominium' && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();

                              if (isInComparison(property.id)) {
                                await removeFromComparison(property.id);
                                showNotification('Imóvel removido da comparação', 'warning', 3000);
                              } else {
                                const result = await addToComparison(property);
                                if (result.success) {
                                  showNotification(result.message, 'success', 3000);
                                } else {
                                  showNotification(result.message, 'error', 5000);
                                }
                              }
                            }}
                            className={`p-1 rounded-full hover:bg-gray-100 transition-colors group relative ${
                              isInComparison(property.id) ? 'bg-white' : ''
                            }`}
                            title={isInComparison(property.id) ? "Remover da comparação" : "Adicionar à comparação"}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill={isInComparison(property.id) ? "#3B82F6" : "none"}
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className={`w-3.5 h-3.5 transition-colors ${
                                isInComparison(property.id)
                                  ? "text-blue-500"
                                  : "text-gray-400 group-hover:text-blue-500"
                              }`}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                          </button>
                          )}

                          {/* Compartilhar */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShareModal({ isOpen: true, property });
                            }}
                            className="p-1 rounded-full hover:bg-gray-100 transition-colors group relative"
                            title="Compartilhar"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-3.5 h-3.5 text-gray-400 group-hover:text-green-500 transition-colors"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.935-2.186 2.25 2.25 0 00-3.935-2.186z"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <h3 
                        className="font-montserrat font-semibold text-[#444444] line-clamp-2 cursor-pointer transition-colors duration-200 hover:text-[var(--primary-color,#3B82F6)] text-[1.1rem] mb-2"
                        onClick={() => navigate(detailPath(property))}
                      >
                        {property.title}
                      </h3>
                    </div>

                    <div>
                      <p className="font-lato text-gray-600 mb-4 flex items-center gap-2 text-[13px]">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                        </svg>
                        {property.address}
                      </p>

                      {/* Grid de informações */}
                      <div className="grid grid-cols-2 gap-4 text-sm font-lato text-gray-500">
                        <div className="flex items-center gap-3">
                          <i className="flaticon-bed text-lg"></i>
                          <span>{property.bedrooms} Quartos</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <i className="flaticon-bathtub text-lg"></i>
                          <span>{property.bathrooms} Banheiros</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <i className="flaticon-square text-lg"></i>
                          <span>{property.area}m² priv.</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <i className="flaticon-car text-lg"></i>
                          <span>{property.parkingSpaces} Vagas</span>
                        </div>
                      </div>

                      {/* Linha divisória */}
                      <div className="border-t border-gray-200 my-4"></div>

                      {/* Botões */}
                      <div className="flex flex-col gap-2">
                        <button 
                          onClick={() => navigate(detailPath(property))}
                          className="w-full rounded-lg py-2 transition-all duration-300"
                          style={{
                            backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                            color: 'var(--btn-outline-text, #3B82F6)',
                            border: '1px solid var(--btn-outline-border, #3B82F6)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
                            e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
                            e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
                          }}
                        >
                          Ver detalhes
                        </button>
                        <button
                          type="button"
                          onClick={() => removeFavorite(property)}
                          className="w-full rounded-lg py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Botão Ver Todas as Propriedades - só aparece quando há favoritos */}
          {favorites.length > 0 && (
            <div 
              className={`mt-12 text-center transition-all duration-1000 delay-500 ${visibleElements.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <button
                onClick={() => navigate('/propriedades')}
                className="px-8 py-3 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 mx-auto"
                style={{
                  backgroundColor: 'var(--primary-color, #3B82F6)',
                  color: 'var(--secondary-color, #ffffff)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                </svg>
                Ver todas as propriedades
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Compartilhamento */}
      {shareModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShareModal({ isOpen: false, property: null })}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4" 
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">
                {shareModal.property?.favoriteEntity === 'condominium' ? 'Compartilhar condomínio' : 'Compartilhar imóvel'}
              </h3>
              <button
                onClick={() => setShareModal({ isOpen: false, property: null })}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>{shareModal.property?.title}</strong>
              </p>
              <p className="text-xs text-gray-500">
                {shareModal.property?.address}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* WhatsApp */}
              <button
                onClick={() => {
                  shareToWhatsApp(shareModal.property);
                  setShareModal({ isOpen: false, property: null });
                }}
                className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-green-50 hover:border-green-500 transition-colors"
              >
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                <span className="text-sm font-medium">WhatsApp</span>
              </button>

              {/* Facebook */}
              <button
                onClick={() => {
                  shareToFacebook(shareModal.property);
                  setShareModal({ isOpen: false, property: null });
                }}
                className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-[#1877F2]/10 hover:border-[#1877F2] transition-colors"
              >
                <svg className="w-6 h-6" fill="#1877F2" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="text-sm font-medium">Facebook</span>
              </button>

              {/* LinkedIn */}
              <button
                onClick={() => {
                  shareToLinkedIn(shareModal.property);
                  setShareModal({ isOpen: false, property: null });
                }}
                className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-[#0A66C2]/10 hover:border-[#0A66C2] transition-colors"
              >
                <svg className="w-6 h-6" fill="#0A66C2" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                <span className="text-sm font-medium">LinkedIn</span>
              </button>

              {/* Email */}
              <button
                onClick={() => {
                  shareToEmail(shareModal.property);
                  setShareModal({ isOpen: false, property: null });
                }}
                className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium">Email</span>
              </button>
            </div>

            {/* Copiar Link */}
            <button
              onClick={() => {
                copyLink(shareModal.property);
                setShareModal({ isOpen: false, property: null });
              }}
              className="w-full mt-4 flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium">Copiar Link</span>
            </button>
          </div>
        </div>
      )}

      {/* Modal de Confirmação para Limpar Lista */}
      {clearModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setClearModal({ isOpen: false })}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4" 
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-xl font-semibold mb-4">Confirmar Limpeza</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja remover todos os imóveis da sua lista de favoritos? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-between gap-3">
              <button
                onClick={() => setClearModal({ isOpen: false })}
                className="px-4 py-2 rounded-lg transition-all duration-300"
                style={{
                  backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                  color: 'var(--btn-outline-text, #3B82F6)',
                  border: '1px solid var(--btn-outline-border, #3B82F6)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
                  e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
                  e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmClearAllFavorites}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
              >
                Limpar Lista
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Compartilhamento de Lista */}
      {shareListModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShareListModal({ isOpen: false, shareUrl: '' })}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4" 
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Compartilhar Lista</h3>
              <button
                onClick={() => setShareListModal({ isOpen: false, shareUrl: '' })}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Lista de Favoritos de {user?.name || 'Usuário'}</strong>
              </p>
              <p className="text-xs text-gray-500 mb-4">
                {favorites.length} imóveis na lista
              </p>
              
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-xs text-gray-500 mb-1">Link público:</p>
                <p className="text-sm text-gray-700 break-all">{shareListModal.shareUrl}</p>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={copyShareLink}
                className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium">Copiar Link</span>
              </button>

              <button
                onClick={shareListToWhatsApp}
                className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-green-50 hover:border-green-500 transition-colors"
              >
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                <span className="text-sm font-medium">Enviar no WhatsApp</span>
              </button>
            </div>

            <div className="mt-4 p-3 bg-white rounded-lg">
              <p className="text-xs text-black">
                <strong>Dica:</strong> Este link é público e pode ser acessado por qualquer pessoa. A lista expira em <span className="text-red-600 font-semibold">24 horas</span>.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <ScrollToTop />
    </div>
  );
}

export default Favorites;
